import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api.js';
import { useParams } from 'react-router-dom';
import { TimeField, formatStamp } from '../lib/timeFields.jsx';
import { useVenueTz } from '@/lib/venueTz.js';

const DOW_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function Templates() {
  const { tenantSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = () => {
    setLoading(true);
    apiFetch(`/api/tenants/${tenantSlug}/templates`)
      .then((r) => r.json())
      .then((d) => { if (d.error) throw new Error(d.error); setData(d); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tenantSlug]);

  if (loading) return <div className="text-muted-foreground">Loading…</div>;
  if (error) return <div className="text-destructive">Error: {error}</div>;

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">Templates</h1>

      <section className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">DOW assignments</h2>
        <div className="border border-border rounded-md p-3 bg-card overflow-x-auto">
          <div className="grid grid-cols-7 gap-2 min-w-[34rem]">
            {[0,1,2,3,4,5,6].map((dow) => {
              const a = data.dow_assignments.find((x) => x.day_of_week === dow);
              return (
                <div key={dow} className="text-center">
                  <div className="text-xs font-medium text-muted-foreground">{DOW_NAMES[dow]}</div>
                  <div className="text-xs mt-1">{a ? a.template_name : <span className="text-muted-foreground">closed</span>}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">
          Big-date overrides ({data.date_overrides.length})
        </h2>
        <div className="md:hidden space-y-2">
          {data.date_overrides.map((d, i) => (
            <div key={i} className="border border-border rounded-md p-3 bg-card text-sm">
              <div className="font-medium">{d.template_name}</div>
              <div className="text-xs text-muted-foreground">
                {new Date(d.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}{d.reason ? ` · ${d.reason}` : ''}
              </div>
            </div>
          ))}
          {data.date_overrides.length === 0 && <div className="text-center text-muted-foreground py-4 text-sm">No overrides.</div>}
        </div>
        <div className="border border-border rounded-md overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Template</th>
                <th className="px-3 py-2 font-medium">Reason</th>
              </tr>
            </thead>
            <tbody>
              {data.date_overrides.map((d, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="px-3 py-2 font-mono text-xs">{d.date}</td>
                  <td className="px-3 py-2">{d.template_name}</td>
                  <td className="px-3 py-2 text-muted-foreground">{d.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-2">
          All templates ({data.templates.length})
        </h2>
        <div className="md:hidden space-y-2">
          {data.templates.map((t) => {
            const flyer = (t.media || []).find((m) => m.kind === 'flyer' && m.is_primary && m.media_type === 'image');
            return (
              <div key={t.id} onClick={() => setOpenId(openId === t.id ? null : t.id)} className={`border border-border rounded-md p-3 bg-card text-sm cursor-pointer active:bg-accent/40 ${openId === t.id ? 'ring-1 ring-primary/50' : ''}`}>
                <div className="flex items-start gap-3">
                  <TemplateThumb url={flyer?.url} name={t.name} />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{t.name}</div>
                    {t.talent && <div className="text-xs text-muted-foreground truncate">{t.talent}</div>}
                    <div className="text-xs text-muted-foreground mt-1">{t.tier_count} tiers · {t.upcoming_event_count} upcoming · {t.event_count} all</div>
                    {(t.publish_defaults || []).length > 0 && <div className="text-xs text-muted-foreground font-mono truncate">{(t.publish_defaults || []).map((p) => p.platform).join(', ')}</div>}
                  </div>
                </div>
              </div>
            );
          })}
          {data.templates.length === 0 && <div className="text-center text-muted-foreground py-4 text-sm">No templates.</div>}
        </div>
        <div className="border border-border rounded-md overflow-x-auto hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-2 py-2 font-medium w-14"></th>
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Talent</th>
                <th className="px-3 py-2 font-medium text-right">Tiers</th>
                <th className="px-3 py-2 font-medium text-right">Upcoming</th>
                <th className="px-3 py-2 font-medium text-right">All events</th>
                <th className="px-3 py-2 font-medium">Publish defaults</th>
              </tr>
            </thead>
            <tbody>
              {data.templates.map((t) => {
                const flyer = (t.media || []).find((m) => m.kind === 'flyer' && m.is_primary && m.media_type === 'image');
                return (
                  <tr key={t.id} className="border-t border-border hover:bg-accent/30 cursor-pointer" onClick={() => setOpenId(openId === t.id ? null : t.id)}>
                    <td className="px-2 py-1.5">
                      <TemplateThumb url={flyer?.url} name={t.name} />
                    </td>
                    <td className="px-3 py-2">{t.name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{t.talent}</td>
                    <td className="px-3 py-2 text-right">{t.tier_count}</td>
                    <td className="px-3 py-2 text-right font-medium">{t.upcoming_event_count}</td>
                    <td className="px-3 py-2 text-right text-muted-foreground">{t.event_count}</td>
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{(t.publish_defaults || []).map((p) => p.platform).join(', ')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {openId && <TemplateDetail tenantSlug={tenantSlug} templateId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
      </section>
    </div>
  );
}

function TemplateThumb({ url, name }) {
  if (url) {
    return <img src={url} alt="" loading="lazy" className="w-10 h-10 rounded object-cover bg-muted" />;
  }
  const initials = (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';
  const hue = Math.abs((name || '').split('').reduce((a, c) => a * 31 + c.charCodeAt(0), 7)) % 360;
  return (
    <div className="w-10 h-10 rounded flex items-center justify-center text-xs font-semibold text-white"
         style={{ background: `hsl(${hue} 55% 45%)` }}>{initials}</div>
  );
}

function TemplateDetail({ tenantSlug, templateId, onClose, onChanged }) {
  const [t, setT] = useState(null);
  const [draft, setDraft] = useState(null);
  const [err, setErr] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const load = () => apiFetch(`/api/tenants/${tenantSlug}/templates/${templateId}`).then((r) => r.json()).then(setT);
  useEffect(load, [tenantSlug, templateId]);
  if (!t) return null;
  if (t.error) return <div className="text-destructive mt-2">Error: {t.error}</div>;

  const flyer  = (t.media || []).find((m) => m.kind === 'flyer'  && m.is_primary && m.media_type === 'image');
  const banner = (t.media || []).find((m) => m.kind === 'banner' && m.is_primary && m.media_type === 'image');
  const cover  = (t.media || []).find((m) => m.kind === 'cover'  && m.is_primary && m.media_type === 'image');

  const uploadMedia = async (file, kind, mediaId = null) => {
    setUploading(true); setUploadError(null);
    try {
      if (file.size > 5 * 1024 * 1024) throw new Error('File over 5 MB limit');
      const fd = new FormData(); fd.append('file', file);
      const qs = new URLSearchParams({ kind });
      if (mediaId) qs.set('media_id', mediaId);
      const res = await apiFetch(`/api/tenants/${tenantSlug}/templates/${templateId}/media?${qs}`, { method: 'PUT', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setUploadError(e.message);
    } finally {
      setUploading(false);
    }
  };
  const deleteMedia = async (mediaId) => {
    if (!confirm('Remove this default media?')) return;
    try {
      const res = await apiFetch(`/api/tenants/${tenantSlug}/templates/${templateId}/media/${mediaId}`, { method: 'DELETE' });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'delete failed'); }
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setUploadError(e.message);
    }
  };

  const startEdit = () => setDraft(t.tiers.map((x) => ({
    name: x.name, gender: x.gender || '', release_step: x.release_step ?? '',
    price_cents: x.price_cents, capacity: x.capacity ?? '',
  })));
  const save = async () => {
    setErr(null);
    try {
      const body = { tiers: draft.map((x, i) => ({
        name: x.name,
        gender: x.gender || null,
        release_step: x.release_step === '' ? null : Number(x.release_step),
        price_cents: Number(x.price_cents),
        capacity: x.capacity === '' ? null : Number(x.capacity),
        sort_index: i,
      })) };
      const res = await apiFetch(`/api/tenants/${tenantSlug}/templates/${templateId}/tiers`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setDraft(null);
      load();
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <div className="mt-3 border border-border rounded-md p-4 bg-card text-sm space-y-4">
      <div className="flex items-center justify-between">
        <div className="font-medium">{t.name} · {t.venue_name}</div>
        <div className="flex items-center gap-2">
          {err && <span className="text-xs text-destructive">{err}</span>}
          {draft ? (
            <>
              <button onClick={() => setDraft((d) => [...d, { name: 'New tier', gender: '', release_step: '', price_cents: 0, capacity: '' }])} className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent">+ Add</button>
              <button onClick={() => setDraft(null)} className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent">Cancel</button>
              <button onClick={save} className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:opacity-90">Save</button>
            </>
          ) : (
            <button onClick={startEdit} className="px-2 py-1 text-xs border border-input rounded-md hover:bg-accent">Edit tiers</button>
          )}
          <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground">close</button>
        </div>
      </div>

      <div className="text-xs text-muted-foreground">talent: {t.talent} · age: {t.default_age_limit ?? '—'} · doors offset: {t.default_doors_offset_minutes ?? '—'} min · pricing: {t.default_pricing_mode}</div>

      <div>
        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Default media (inherited by linked events)</div>
        {uploadError && <div className="text-xs text-destructive mb-2">{uploadError}</div>}
        <div className="grid grid-cols-3 gap-3">
          <TemplateMediaSlot label="Flyer (square)"  kind="flyer"  media={flyer}  uploading={uploading} onUpload={uploadMedia} onDelete={deleteMedia} aspect="aspect-square" />
          <TemplateMediaSlot label="Banner (header)" kind="banner" media={banner} uploading={uploading} onUpload={uploadMedia} onDelete={deleteMedia} aspect="aspect-[16/9]" />
          <TemplateMediaSlot label="Cover"           kind="cover"  media={cover}  uploading={uploading} onUpload={uploadMedia} onDelete={deleteMedia} aspect="aspect-square" />
        </div>
      </div>

      <UrvenueIntegrationPanel tenantSlug={tenantSlug} templateId={templateId} profile={t.integration_profile?.urvenue || {}} onSaved={load} />

      <UniverseTemplatePanel tenantSlug={tenantSlug} templateId={templateId} profile={t.integration_profile?.universe || {}} onSaved={load} />

      <div>
        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Tiers</div>
        <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-1">Tier</th><th className="py-1">Gender</th><th className="py-1">Release</th><th className="py-1 text-right">Price (¢)</th><th className="py-1 text-right">Capacity</th>{draft && <th className="py-1 w-6"></th>}
            </tr>
          </thead>
          <tbody>
            {draft ? draft.map((tier, i) => (
              <tr key={i} className="border-t border-border">
                <td className="py-1"><input value={tier.name} onChange={(e) => setDraft((d) => d.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} className="w-full px-1 border border-input rounded bg-background" /></td>
                <td className="py-1">
                  <select value={tier.gender} onChange={(e) => setDraft((d) => d.map((x, j) => j === i ? { ...x, gender: e.target.value } : x))} className="w-full px-1 border border-input rounded bg-background text-xs">
                    <option value="">—</option><option value="female">female</option><option value="male">male</option>
                  </select>
                </td>
                <td className="py-1 w-16"><input type="number" value={tier.release_step} onChange={(e) => setDraft((d) => d.map((x, j) => j === i ? { ...x, release_step: e.target.value } : x))} className="w-full px-1 border border-input rounded bg-background" /></td>
                <td className="py-1 w-24"><input type="number" value={tier.price_cents} onChange={(e) => setDraft((d) => d.map((x, j) => j === i ? { ...x, price_cents: e.target.value } : x))} className="w-full px-1 border border-input rounded bg-background" /></td>
                <td className="py-1 w-20"><input type="number" value={tier.capacity} onChange={(e) => setDraft((d) => d.map((x, j) => j === i ? { ...x, capacity: e.target.value } : x))} className="w-full px-1 border border-input rounded bg-background" /></td>
                <td className="py-1 text-right"><button onClick={() => setDraft((d) => d.filter((_, j) => j !== i))} className="text-destructive">×</button></td>
              </tr>
            )) : t.tiers.map((tier) => (
              <tr key={tier.id} className="border-t border-border">
                <td className="py-1">{tier.name}</td>
                <td className="py-1 text-muted-foreground">{tier.gender || '—'}</td>
                <td className="py-1 text-muted-foreground">{tier.release_step ?? '—'}</td>
                <td className="py-1 text-right">${(tier.price_cents / 100).toFixed(2)}</td>
                <td className="py-1 text-right">{tier.capacity ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function TemplateMediaSlot({ label, kind, media, uploading, onUpload, onDelete, aspect = 'aspect-square' }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <label className="text-xs text-primary cursor-pointer hover:underline">
          {uploading ? 'Uploading…' : media ? 'Replace…' : 'Upload…'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            disabled={uploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f, kind, media?.id);
              e.target.value = '';
            }}
          />
        </label>
      </div>
      {media ? (
        <div className="relative">
          <img src={media.url} alt="" className={`w-full rounded-md bg-muted ${aspect} object-cover`} />
          <button
            onClick={() => onDelete(media.id)}
            className="absolute top-1 right-1 text-[10px] px-1.5 py-0.5 bg-background/90 border border-border rounded text-destructive hover:bg-destructive hover:text-destructive-foreground"
          >Remove</button>
          <div className="text-[10px] text-muted-foreground mt-1">{media.source || 'manual'}</div>
        </div>
      ) : (
        <div className={`w-full ${aspect} rounded-md bg-muted flex items-center justify-center text-xs text-muted-foreground`}>
          no {kind}
        </div>
      )}
    </div>
  );
}

function UrvenueIntegrationPanel({ tenantSlug, templateId, profile, onSaved }) {
  const tz = useVenueTz();
  const [templates, setTemplates] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  // Full UrVenue template config — was 4 fields, now the complete surface
  // the publisher / resolver consume (event_type_id, times, marketareaid,
  // shifts, tz, ...). Empty values = inherit from venue.
  const [draft, setDraft] = useState({
    inventory_template_id: profile.inventory_template_id ?? '',
    open_24h: !!profile.open_24h,
    place_id: profile.place_id ?? 0,
    ecozone: profile.ecozone ?? 0,
    event_type_id: profile.event_type_id ?? '',
    default_start_time: profile.default_start_time ?? '',
    default_end_time: profile.default_end_time ?? '',
    default_doors_open: profile.default_doors_open ?? '',
    marketareaid: profile.marketareaid ?? '',
    shifts: profile.shifts ?? '',
    tz: profile.tz ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    apiFetch(`/api/tenants/${tenantSlug}/urvenue/inventory-templates`)
      .then(r => r.json())
      .then(d => { if (d.error) throw new Error(d.error); setTemplates(d.templates || []); })
      .catch(e => setLoadErr(e.message));
  }, [tenantSlug]);

  // Saves via the new urvenueRouter PATCH (SAFE-key validation + audit
  // context). Falls back to the legacy /templates/:id PATCH if the new
  // endpoint isn't available (defensive — shouldn't happen).
  const save = async () => {
    setSaving(true); setSaveErr(null);
    try {
      const body = {
        inventory_template_id: draft.inventory_template_id === '' ? null : Number(draft.inventory_template_id),
        open_24h: !!draft.open_24h,
        place_id: Number(draft.place_id) || 0,
        ecozone: Number(draft.ecozone) || 0,
        event_type_id: draft.event_type_id.trim() || null,
        default_start_time: draft.default_start_time.trim() || null,
        default_end_time: draft.default_end_time.trim() || null,
        default_doors_open: draft.default_doors_open.trim() || null,
        marketareaid: draft.marketareaid.trim() || null,
        shifts: draft.shifts.trim() || null,
        tz: draft.tz.trim() || null,
      };
      const res = await apiFetch(`/api/tenants/${tenantSlug}/urvenue/templates/${templateId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || d.error || 'save failed');
      setSavedAt(new Date());
      if (onSaved) onSaved();
    } catch (e) {
      setSaveErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-border pt-3">
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Integrations → UrVenue</div>
      {loadErr && <div className="text-xs text-destructive mb-2">Failed to load UrVenue templates: {loadErr}</div>}
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <label className="block text-muted-foreground mb-1">Inventory template (UrVenue)</label>
          <select
            value={draft.inventory_template_id}
            onChange={(e) => setDraft(d => ({ ...d, inventory_template_id: e.target.value }))}
            className="w-full px-2 py-1 border border-input rounded-md bg-background"
            disabled={!templates}
          >
            <option value="">· None (skip inventory) —</option>
            {(templates || []).map(t => (
              <option key={t.templateid || t.id} value={t.templateid || t.id}>
                {t.name || t.label} ({t.templateid || t.id})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-muted-foreground mb-1">Event type id</label>
          <input value={draft.event_type_id} placeholder="venue default" onChange={(e) => setDraft(d => ({ ...d, event_type_id: e.target.value }))} className="w-full px-2 py-1 border border-input rounded-md bg-background font-mono" />
        </div>
        <div>
          <label className="block text-muted-foreground mb-1">Default start time <span className="font-normal opacity-70">(venue default if blank)</span></label>
          <TimeField value={draft.default_start_time} onChange={(v) => setDraft(d => ({ ...d, default_start_time: v || '' }))} className="w-full px-2 py-1 border border-input rounded-md bg-background" />
        </div>
        <div>
          <label className="block text-muted-foreground mb-1">Default end time <span className="font-normal opacity-70">(venue default if blank)</span></label>
          <TimeField value={draft.default_end_time} onChange={(v) => setDraft(d => ({ ...d, default_end_time: v || '' }))} className="w-full px-2 py-1 border border-input rounded-md bg-background" />
        </div>
        <div>
          <label className="block text-muted-foreground mb-1">Default doors open <span className="font-normal opacity-70">(optional)</span></label>
          <TimeField value={draft.default_doors_open} onChange={(v) => setDraft(d => ({ ...d, default_doors_open: v || '' }))} className="w-full px-2 py-1 border border-input rounded-md bg-background" />
        </div>
        <div>
          <label className="block text-muted-foreground mb-1">Marketareaid</label>
          <input value={draft.marketareaid} placeholder="venue default" onChange={(e) => setDraft(d => ({ ...d, marketareaid: e.target.value }))} className="w-full px-2 py-1 border border-input rounded-md bg-background font-mono" />
        </div>
        <div>
          <label className="block text-muted-foreground mb-1">place_id (room)</label>
          <input type="number" value={draft.place_id} onChange={(e) => setDraft(d => ({ ...d, place_id: e.target.value }))} className="w-full px-2 py-1 border border-input rounded-md bg-background" />
        </div>
        <div>
          <label className="block text-muted-foreground mb-1">ecozone</label>
          <input type="number" value={draft.ecozone} onChange={(e) => setDraft(d => ({ ...d, ecozone: e.target.value }))} className="w-full px-2 py-1 border border-input rounded-md bg-background" />
        </div>
        <div>
          <label className="block text-muted-foreground mb-1">Shift schedule override</label>
          <input value={draft.shifts} placeholder="leave blank unless overriding venue shifts" onChange={(e) => setDraft(d => ({ ...d, shifts: e.target.value }))} className="w-full px-2 py-1 border border-input rounded-md bg-background font-mono" />
        </div>
        <div>
          <label className="block text-muted-foreground mb-1">Time zone</label>
          <input value={draft.tz} placeholder="America/New_York (venue default if blank)" onChange={(e) => setDraft(d => ({ ...d, tz: e.target.value }))} className="w-full px-2 py-1 border border-input rounded-md bg-background font-mono" />
        </div>
        <div className="col-span-2">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={draft.open_24h} onChange={(e) => setDraft(d => ({ ...d, open_24h: e.target.checked }))} />
            <span>Open day for 24h on push (override DOW closure)</span>
          </label>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <button onClick={save} disabled={saving} className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
        {saveErr && <span className="text-xs text-destructive">{saveErr}</span>}
        {savedAt && !saveErr && <span className="text-xs text-emerald-600">saved {formatStamp(savedAt, tz)}</span>}
      </div>
    </div>
  );
}

// === UniverseTemplatePanel =================================================
// Configures event_templates.integration_profile.universe.placeholder — the
// canonical structure that every new Universe listing inherits. Carries the
// 13-rate ladder, image uploadcareId set, host fields, rate upgrades, and
// the residency description/private note.
//
// Calibration import: paste a known-intact Universe listing id, click
// "Import" — backend extractTemplatePlaceholder pulls all the above in one
// shot. From there, ops can edit any field.

function UniverseTemplatePanel({ tenantSlug, templateId, profile, onSaved }) {
  const tz = useVenueTz();
  const placeholder = profile.placeholder || {};
  const [listingId, setListingId] = useState('');
  const [importing, setImporting] = useState(false);
  const [importErr, setImportErr] = useState(null);
  const [importedAt, setImportedAt] = useState(null);
  const [showJson, setShowJson] = useState(false);
  const [editing, setEditing] = useState(null);  // 'description' | 'private_note' | 'rate_ladder' | 'host_fields' | 'rate_upgrades'
  const [saveErr, setSaveErr] = useState(null);

  const patchPlaceholder = async (patch) => {
    setSaveErr(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/universe/templates/${templateId}/placeholder`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || 'patch_failed');
      setEditing(null);
      if (onSaved) onSaved();
    } catch (e) {
      setSaveErr(e.message);
      throw e;
    }
  };

  const doImport = async () => {
    if (!listingId.trim()) return;
    setImporting(true); setImportErr(null);
    try {
      const r = await apiFetch(`/api/tenants/${tenantSlug}/universe/calibrate-template`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_id: templateId, listing_id: listingId.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || 'import_failed');
      setImportedAt(new Date());
      if (onSaved) onSaved();
    } catch (e) {
      setImportErr(e.message);
    } finally {
      setImporting(false);
    }
  };

  const imgs = placeholder.images || {};
  const ladder = placeholder.rate_ladder || [];
  const fields = placeholder.host_fields || [];
  const upgrades = placeholder.rate_upgrades || [];

  return (
    <div className="border-t border-border pt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Integrations → Universe</div>
        {placeholder._extracted_from?.extracted_at && (
          <div className="text-[10px] text-muted-foreground">
            last extracted {formatStamp(placeholder._extracted_from.extracted_at, tz)} from <code>{placeholder._extracted_from.listing_id}</code>
          </div>
        )}
      </div>

      <div className="flex items-end gap-2 mb-3">
        <div className="flex-1">
          <label className="block text-xs text-muted-foreground mb-1">Import from Universe listing ID</label>
          <input
            value={listingId}
            onChange={(e) => setListingId(e.target.value)}
            placeholder="e.g. 1MPV0L or 6a17b1bbcd50d30027e3eb82"
            className="w-full px-2 py-1 text-xs border border-input rounded-md bg-background font-mono"
          />
        </div>
        <button
          onClick={doImport}
          disabled={importing || !listingId.trim()}
          className="px-3 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:opacity-90 disabled:opacity-50"
        >{importing ? 'Importing…' : 'Import'}</button>
      </div>
      {importErr && <div className="text-xs text-destructive mb-2">{importErr}</div>}
      {importedAt && <div className="text-xs text-emerald-600 mb-2">Imported {formatStamp(importedAt, tz)}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-3">
        <div className="border border-border rounded p-2">
          <div className="text-muted-foreground text-[10px] uppercase">Description</div>
          <div className="text-lg font-medium">{placeholder.description_html?.length || 0}</div>
          <div className="text-[10px] text-muted-foreground">chars</div>
        </div>
        <div className="border border-border rounded p-2">
          <div className="text-muted-foreground text-[10px] uppercase">Rate ladder</div>
          <div className="text-lg font-medium">{ladder.length}</div>
          <div className="text-[10px] text-muted-foreground">rates</div>
        </div>
        <div className="border border-border rounded p-2">
          <div className="text-muted-foreground text-[10px] uppercase">Images</div>
          <div className="text-lg font-medium">
            {(imgs.cover_uploadcare_id ? 1 : 0) + (imgs.event_uploadcare_id ? 1 : 0) + (imgs.additional_uploadcare_ids?.length || 0)}
          </div>
          <div className="text-[10px] text-muted-foreground">uploadcare ids</div>
        </div>
        <div className="border border-border rounded p-2">
          <div className="text-muted-foreground text-[10px] uppercase">Host fields · Upgrades</div>
          <div className="text-lg font-medium">{fields.length} · {upgrades.length}</div>
        </div>
      </div>

      {saveErr && <div className="text-xs text-destructive mb-2">{saveErr}</div>}

      <PlaceholderTextSection
        label="Description (descriptionHtml)"
        value={placeholder.description_html || ''}
        editing={editing === 'description'}
        onEdit={() => setEditing('description')}
        onCancel={() => setEditing(null)}
        onSave={(v) => patchPlaceholder({ description_html: v })}
      />

      <PlaceholderTextSection
        label="Private note (privateNote)"
        value={placeholder.private_note_html || ''}
        editing={editing === 'private_note'}
        onEdit={() => setEditing('private_note')}
        onCancel={() => setEditing(null)}
        onSave={(v) => patchPlaceholder({ private_note_html: v })}
      />

      <PlaceholderImagesSection
        images={imgs}
        editing={editing === 'images'}
        onEdit={() => setEditing('images')}
        onCancel={() => setEditing(null)}
        onSave={(v) => patchPlaceholder({ images: v })}
      />

      <RateLadderEditor
        ladder={ladder}
        editing={editing === 'rate_ladder'}
        onEdit={() => setEditing('rate_ladder')}
        onCancel={() => setEditing(null)}
        onSave={(v) => patchPlaceholder({ rate_ladder: v })}
      />

      <HostFieldsEditor
        fields={fields}
        editing={editing === 'host_fields'}
        onEdit={() => setEditing('host_fields')}
        onCancel={() => setEditing(null)}
        onSave={(v) => patchPlaceholder({ host_fields: v })}
      />

      <RateUpgradesEditor
        upgrades={upgrades}
        ladder={ladder}
        editing={editing === 'rate_upgrades'}
        onEdit={() => setEditing('rate_upgrades')}
        onCancel={() => setEditing(null)}
        onSave={(v) => patchPlaceholder({ rate_upgrades: v })}
      />

      <button
        onClick={() => setShowJson((s) => !s)}
        className="text-xs text-muted-foreground hover:text-foreground"
      >{showJson ? 'Hide' : 'Show'} raw JSON</button>
      {showJson && (
        <pre className="mt-2 p-2 bg-muted/30 rounded overflow-x-auto text-[10px]">
          {JSON.stringify(profile, null, 2)}
        </pre>
      )}
    </div>
  );
}

// --- Template placeholder field editors ---

function PlaceholderTextSection({ label, value, editing, onEdit, onCancel, onSave }) {
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { if (editing) setDraft(value || ''); }, [editing, value]);
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
        {editing ? (
          <div className="flex gap-1">
            <button onClick={onCancel} className="text-[10px] px-1.5 py-0.5 border border-input rounded">cancel</button>
            <button onClick={() => onSave(draft)} className="text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground rounded">save</button>
          </div>
        ) : (
          <button onClick={onEdit} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">edit</button>
        )}
      </div>
      {editing ? (
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={8}
          className="w-full px-2 py-1 border border-input rounded-md bg-background font-mono text-[11px]"
        />
      ) : value ? (
        <div className="border border-border rounded p-2 text-[11px] max-h-32 overflow-y-auto whitespace-pre-wrap">
          {value.length > 400 ? value.slice(0, 400) + '…' : value}
        </div>
      ) : (
        <div className="text-[11px] text-muted-foreground italic">empty</div>
      )}
    </div>
  );
}

// Uploadcare direct-upload helper. Returns the uuid; no SDK required.
// Pub key is Universe's public Uploadcare project key (extracted from the
// seller portal config) — uploads go straight into the Universe-shared
// CDN namespace so the same uuid drops in as ImageInputType.uploadId.
const UPLOADCARE_PUB_KEY = '6983c6f19fcba1adfe3d';
async function uploadcareUpload(file) {
  const fd = new FormData();
  fd.append('UPLOADCARE_PUB_KEY', UPLOADCARE_PUB_KEY);
  fd.append('UPLOADCARE_STORE', '1');
  fd.append('file', file);
  const r = await fetch('https://upload.uploadcare.com/base/', { method: 'POST', body: fd });
  if (!r.ok) throw new Error(`uploadcare upload failed: ${r.status}`);
  const d = await r.json();
  if (!d.file) throw new Error('uploadcare returned no file uuid');
  return d.file;
}

function UploadcarePicker({ value, onChange, label = 'uploadId', aspect = 'aspect-square' }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const onFile = async (file) => {
    if (!file) return;
    setBusy(true); setErr(null);
    try {
      const uuid = await uploadcareUpload(file);
      onChange(uuid);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <label className="text-muted-foreground text-[11px]">{label}</label>
        <label className="text-[10px] text-primary cursor-pointer hover:underline">
          {busy ? 'uploading…' : value ? 'replace' : 'upload'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={busy}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
              e.target.value = '';
            }}
          />
        </label>
      </div>
      <input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="paste an existing uploadcare uuid or upload above"
        className="w-full px-2 py-1 border border-input rounded-md bg-background font-mono text-[11px]"
      />
      {err && <div className="text-[10px] text-destructive">{err}</div>}
      {value && (
        <img
          src={`https://ucarecdn.com/${value}/-/preview/200x200/`}
          alt=""
          className={`w-24 ${aspect} object-cover rounded border border-border`}
        />
      )}
    </div>
  );
}

function AdditionalUploadcarePicker({ ids, onChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const onFiles = async (files) => {
    if (!files?.length) return;
    setBusy(true); setErr(null);
    try {
      const next = [...ids];
      for (const f of Array.from(files)) {
        const uuid = await uploadcareUpload(f);
        next.push(uuid);
      }
      onChange(next);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  const remove = (i) => onChange(ids.filter((_, j) => j !== i));
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-muted-foreground text-[11px]">additionalPhotos uploadIds</label>
        <label className="text-[10px] text-primary cursor-pointer hover:underline">
          {busy ? 'uploading…' : '+ upload (multi-select OK)'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            disabled={busy}
            className="hidden"
            onChange={(e) => {
              onFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
      </div>
      <div className="flex gap-2 flex-wrap">
        {ids.map((id, i) => (
          <div key={id + i} className="relative group">
            <img src={`https://ucarecdn.com/${id}/-/preview/100x100/`} alt="" className="w-16 h-16 rounded border border-border object-cover" />
            <button
              onClick={() => remove(i)}
              className="absolute top-0.5 right-0.5 text-[10px] px-1 bg-background/90 border border-border rounded text-destructive hover:bg-destructive hover:text-destructive-foreground opacity-0 group-hover:opacity-100"
            >×</button>
          </div>
        ))}
      </div>
      {err && <div className="text-[10px] text-destructive mt-1">{err}</div>}
    </div>
  );
}

function PlaceholderImagesSection({ images, editing, onEdit, onCancel, onSave }) {
  const [draft, setDraft] = useState(images || {});
  useEffect(() => { if (editing) setDraft(images || {}); }, [editing, images]);
  const additional = (draft.additional_uploadcare_ids || []).join('\n');
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase text-muted-foreground">Images (Uploadcare UUIDs)</div>
        {editing ? (
          <div className="flex gap-1">
            <button onClick={onCancel} className="text-[10px] px-1.5 py-0.5 border border-input rounded">cancel</button>
            <button onClick={() => onSave({
              event_uploadcare_id: draft.event_uploadcare_id || null,
              cover_uploadcare_id: draft.cover_uploadcare_id || null,
              additional_uploadcare_ids: (draft.additional_uploadcare_ids_text || additional)
                .split(/\s+/).map((s) => s.trim()).filter(Boolean),
            })} className="text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground rounded">save</button>
          </div>
        ) : (
          <button onClick={onEdit} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">edit</button>
        )}
      </div>
      {editing ? (
        <div className="space-y-3 text-[11px]">
          <UploadcarePicker
            label="eventPhoto (square, flyer)"
            value={draft.event_uploadcare_id || ''}
            onChange={(uuid) => setDraft((d) => ({ ...d, event_uploadcare_id: uuid }))}
            aspect="aspect-square"
          />
          <UploadcarePicker
            label="coverPhoto (header banner)"
            value={draft.cover_uploadcare_id || ''}
            onChange={(uuid) => setDraft((d) => ({ ...d, cover_uploadcare_id: uuid }))}
            aspect="aspect-[16/9]"
          />
          <AdditionalUploadcarePicker
            ids={draft.additional_uploadcare_ids_text != null
              ? draft.additional_uploadcare_ids_text.split(/\s+/).map((s) => s.trim()).filter(Boolean)
              : (draft.additional_uploadcare_ids || [])}
            onChange={(ids) => setDraft((d) => ({ ...d, additional_uploadcare_ids: ids, additional_uploadcare_ids_text: ids.join('\n') }))}
          />
        </div>
      ) : (
        <div className="space-y-2 text-[11px]">
          <div className="flex items-center gap-3">
            <span className="text-muted-foreground w-24">eventPhoto:</span>
            {images.event_uploadcare_id
              ? <img src={`https://ucarecdn.com/${images.event_uploadcare_id}/-/preview/100x100/`} alt="" className="w-12 h-12 rounded border border-border object-cover" />
              : <span className="text-muted-foreground">—</span>}
            <code className="text-[10px] text-muted-foreground">{images.event_uploadcare_id || ''}</code>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-muted-foreground w-24">coverPhoto:</span>
            {images.cover_uploadcare_id
              ? <img src={`https://ucarecdn.com/${images.cover_uploadcare_id}/-/preview/200x100/`} alt="" className="w-20 h-10 rounded border border-border object-cover" />
              : <span className="text-muted-foreground">—</span>}
            <code className="text-[10px] text-muted-foreground">{images.cover_uploadcare_id || ''}</code>
          </div>
          <div>
            <span className="text-muted-foreground">additionalPhotos ({images.additional_uploadcare_ids?.length || 0}):</span>
            <div className="flex gap-1 mt-1 flex-wrap">
              {(images.additional_uploadcare_ids || []).map((id) => (
                <img key={id} src={`https://ucarecdn.com/${id}/-/preview/80x80/`} alt="" className="w-12 h-12 rounded border border-border object-cover" title={id} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RateLadderEditor({ ladder, editing, onEdit, onCancel, onSave }) {
  const [draft, setDraft] = useState(ladder);
  useEffect(() => { if (editing) setDraft(ladder.map((r) => ({ ...r }))); }, [editing, ladder]);
  const setRow = (i, k, v) => setDraft((d) => d.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const remove = (i) => setDraft((d) => d.filter((_, j) => j !== i));
  const add = () => setDraft((d) => [...d, {
    ref: '', name: '', description: '', price: 0, capacity: 50,
    min_tickets: null, max_tickets: 6, promotion_pill: null,
    release_offset: 86400, client_group: 'EVERYWHERE', state: 'ACTIVE',
    sort_index: d.length, preemptive_ref: null,
  }]);
  const refs = (editing ? draft : ladder).map((r) => r.ref).filter(Boolean);

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase text-muted-foreground">Rate ladder ({(editing ? draft : ladder).length})</div>
        {editing ? (
          <div className="flex gap-1">
            <button onClick={add} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">+ row</button>
            <button onClick={onCancel} className="text-[10px] px-1.5 py-0.5 border border-input rounded">cancel</button>
            <button onClick={() => onSave(draft.map((r, i) => ({
              ...r,
              sort_index: i,
              price: Number(r.price) || 0,
              capacity: Number(r.capacity) || 0,
              max_tickets: r.max_tickets === '' || r.max_tickets == null ? null : Number(r.max_tickets),
              min_tickets: r.min_tickets === '' || r.min_tickets == null ? null : Number(r.min_tickets),
              release_offset: Number(r.release_offset) || 0,
            })))} className="text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground rounded">save</button>
          </div>
        ) : (
          <button onClick={onEdit} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">edit</button>
        )}
      </div>
      <div className="border border-border rounded overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-muted/40 text-left text-muted-foreground">
            <tr>
              <th className="px-2 py-1">#</th>
              <th className="px-2 py-1">ref</th>
              <th className="px-2 py-1">name</th>
              <th className="px-2 py-1">description</th>
              <th className="px-2 py-1 text-right">price</th>
              <th className="px-2 py-1 text-right">cap</th>
              <th className="px-2 py-1 text-right">max/order</th>
              <th className="px-2 py-1">preemptive</th>
              <th className="px-2 py-1">state</th>
              {editing && <th className="px-2 py-1"></th>}
            </tr>
          </thead>
          <tbody>
            {(editing ? draft : ladder).map((r, i) => (
              <tr key={i} className="border-t border-border align-top">
                <td className="px-2 py-1 text-muted-foreground">{i}</td>
                {editing ? (
                  <>
                    <td className="px-1 py-1"><input value={r.ref || ''} onChange={(e) => setRow(i, 'ref', e.target.value)} className="w-16 px-1 border border-input rounded bg-background font-mono" /></td>
                    <td className="px-1 py-1"><input value={r.name || ''} onChange={(e) => setRow(i, 'name', e.target.value)} className="w-full px-1 border border-input rounded bg-background" /></td>
                    <td className="px-1 py-1"><input value={r.description || ''} onChange={(e) => setRow(i, 'description', e.target.value)} className="w-full px-1 border border-input rounded bg-background" /></td>
                    <td className="px-1 py-1"><input type="number" value={r.price ?? ''} onChange={(e) => setRow(i, 'price', e.target.value)} className="w-16 px-1 border border-input rounded bg-background text-right" /></td>
                    <td className="px-1 py-1"><input type="number" value={r.capacity ?? ''} onChange={(e) => setRow(i, 'capacity', e.target.value)} className="w-16 px-1 border border-input rounded bg-background text-right" /></td>
                    <td className="px-1 py-1"><input type="number" value={r.max_tickets ?? ''} onChange={(e) => setRow(i, 'max_tickets', e.target.value)} className="w-12 px-1 border border-input rounded bg-background text-right" /></td>
                    <td className="px-1 py-1">
                      <select value={r.preemptive_ref || ''} onChange={(e) => setRow(i, 'preemptive_ref', e.target.value || null)} className="w-20 px-1 border border-input rounded bg-background font-mono">
                        <option value="">—</option>
                        {refs.filter((x) => x !== r.ref).map((x) => <option key={x} value={x}>{x}</option>)}
                      </select>
                    </td>
                    <td className="px-1 py-1">
                      <select value={r.state || 'ACTIVE'} onChange={(e) => setRow(i, 'state', e.target.value)} className="px-1 border border-input rounded bg-background">
                        <option>ACTIVE</option><option>INACTIVE</option><option>LOCKED</option><option>HIDDEN</option>
                      </select>
                    </td>
                    <td className="px-1 py-1 text-right"><button onClick={() => remove(i)} className="text-destructive">×</button></td>
                  </>
                ) : (
                  <>
                    <td className="px-2 py-1 font-mono">{r.ref}</td>
                    <td className="px-2 py-1">{r.name}</td>
                    <td className="px-2 py-1 text-muted-foreground">{r.description}</td>
                    <td className="px-2 py-1 text-right">${r.price}</td>
                    <td className="px-2 py-1 text-right">{r.capacity}</td>
                    <td className="px-2 py-1 text-right">{r.max_tickets ?? '—'}</td>
                    <td className="px-2 py-1 font-mono text-muted-foreground">{r.preemptive_ref || '—'}</td>
                    <td className="px-2 py-1 text-muted-foreground">{r.state}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HostFieldsEditor({ fields, editing, onEdit, onCancel, onSave }) {
  const [draft, setDraft] = useState(fields);
  useEffect(() => { if (editing) setDraft(fields.map((h) => ({ ...h }))); }, [editing, fields]);
  const setRow = (i, k, v) => setDraft((d) => d.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const remove = (i) => setDraft((d) => d.filter((_, j) => j !== i));
  const add = () => setDraft((d) => [...d, {
    name: '', type: 'Text', subtype: null, context: 'Ticket',
    required: false, included: true, editable: true, multiple: false,
    sort_index: d.length, has_other: false, text: '', select_options: null,
  }]);

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase text-muted-foreground">Host fields ({(editing ? draft : fields).length})</div>
        {editing ? (
          <div className="flex gap-1">
            <button onClick={add} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">+ row</button>
            <button onClick={onCancel} className="text-[10px] px-1.5 py-0.5 border border-input rounded">cancel</button>
            <button onClick={() => onSave(draft.map((h, i) => ({ ...h, sort_index: i })))} className="text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground rounded">save</button>
          </div>
        ) : (
          <button onClick={onEdit} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">edit</button>
        )}
      </div>
      <div className="border border-border rounded overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-muted/40 text-left text-muted-foreground">
            <tr>
              <th className="px-2 py-1">name</th>
              <th className="px-2 py-1">type</th>
              <th className="px-2 py-1">subtype</th>
              <th className="px-2 py-1">context</th>
              <th className="px-2 py-1">required</th>
              <th className="px-2 py-1">included</th>
              {editing && <th className="px-2 py-1"></th>}
            </tr>
          </thead>
          <tbody>
            {(editing ? draft : fields).map((h, i) => (
              <tr key={i} className="border-t border-border">
                {editing ? (
                  <>
                    <td className="px-1 py-1"><input value={h.name || ''} onChange={(e) => setRow(i, 'name', e.target.value)} className="w-full px-1 border border-input rounded bg-background" /></td>
                    <td className="px-1 py-1">
                      <select value={h.type || 'Text'} onChange={(e) => setRow(i, 'type', e.target.value)} className="px-1 border border-input rounded bg-background">
                        <option>Text</option><option>Checkbox</option><option>Select</option><option>Number</option>
                      </select>
                    </td>
                    <td className="px-1 py-1"><input value={h.subtype || ''} onChange={(e) => setRow(i, 'subtype', e.target.value || null)} className="w-20 px-1 border border-input rounded bg-background" /></td>
                    <td className="px-1 py-1">
                      <select value={h.context || 'Ticket'} onChange={(e) => setRow(i, 'context', e.target.value)} className="px-1 border border-input rounded bg-background">
                        <option>Ticket</option><option>CostItem</option>
                      </select>
                    </td>
                    <td className="px-1 py-1 text-center"><input type="checkbox" checked={!!h.required} onChange={(e) => setRow(i, 'required', e.target.checked)} /></td>
                    <td className="px-1 py-1 text-center"><input type="checkbox" checked={!!h.included} onChange={(e) => setRow(i, 'included', e.target.checked)} /></td>
                    <td className="px-1 py-1 text-right"><button onClick={() => remove(i)} className="text-destructive">×</button></td>
                  </>
                ) : (
                  <>
                    <td className="px-2 py-1">{h.name}</td>
                    <td className="px-2 py-1 text-muted-foreground">{h.type}</td>
                    <td className="px-2 py-1 text-muted-foreground">{h.subtype || '—'}</td>
                    <td className="px-2 py-1 text-muted-foreground">{h.context || '—'}</td>
                    <td className="px-2 py-1 text-center">{h.required ? '✓' : '—'}</td>
                    <td className="px-2 py-1 text-center">{h.included ? '✓' : '—'}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RateUpgradesEditor({ upgrades, ladder, editing, onEdit, onCancel, onSave }) {
  const [draft, setDraft] = useState(upgrades);
  useEffect(() => { if (editing) setDraft(upgrades.map((u) => ({ ...u }))); }, [editing, upgrades]);
  const setRow = (i, k, v) => setDraft((d) => d.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const remove = (i) => setDraft((d) => d.filter((_, j) => j !== i));
  const add = () => setDraft((d) => [...d, {
    target_rate_ref: '', heading: '', subtitle: '', description: '',
    benefits: [], button_text: 'Upgrade Now', enabled: true, image_url: null,
  }]);
  const refs = ladder.map((r) => r.ref).filter(Boolean);

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase text-muted-foreground">Rate upgrades ({(editing ? draft : upgrades).length})</div>
        {editing ? (
          <div className="flex gap-1">
            <button onClick={add} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">+ upgrade</button>
            <button onClick={onCancel} className="text-[10px] px-1.5 py-0.5 border border-input rounded">cancel</button>
            <button onClick={() => onSave(draft)} className="text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground rounded">save</button>
          </div>
        ) : (
          <button onClick={onEdit} className="text-[10px] px-1.5 py-0.5 border border-input rounded hover:bg-accent">edit</button>
        )}
      </div>
      <div className="space-y-2">
        {(editing ? draft : upgrades).map((u, i) => (
          <div key={i} className="border border-border rounded p-2 text-[11px]">
            {editing ? (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-muted-foreground mb-0.5">target rate (ref)</label>
                  <select value={u.target_rate_ref || ''} onChange={(e) => setRow(i, 'target_rate_ref', e.target.value)} className="w-full px-1 border border-input rounded bg-background font-mono">
                    <option value="">—</option>
                    {refs.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-muted-foreground mb-0.5">enabled</label>
                  <input type="checkbox" checked={u.enabled !== false} onChange={(e) => setRow(i, 'enabled', e.target.checked)} />
                </div>
                <div className="col-span-2">
                  <label className="block text-muted-foreground mb-0.5">heading</label>
                  <input value={u.heading || ''} onChange={(e) => setRow(i, 'heading', e.target.value)} className="w-full px-1 border border-input rounded bg-background" />
                </div>
                <div className="col-span-2">
                  <label className="block text-muted-foreground mb-0.5">subtitle</label>
                  <input value={u.subtitle || ''} onChange={(e) => setRow(i, 'subtitle', e.target.value)} className="w-full px-1 border border-input rounded bg-background" />
                </div>
                <div className="col-span-2">
                  <label className="block text-muted-foreground mb-0.5">description</label>
                  <textarea value={u.description || ''} onChange={(e) => setRow(i, 'description', e.target.value)} rows={2} className="w-full px-1 border border-input rounded bg-background" />
                </div>
                <div className="col-span-2">
                  <label className="block text-muted-foreground mb-0.5">benefits (one per line)</label>
                  <textarea
                    value={(u.benefits || []).join('\n')}
                    onChange={(e) => setRow(i, 'benefits', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}
                    rows={3} className="w-full px-1 border border-input rounded bg-background" />
                </div>
                <div>
                  <label className="block text-muted-foreground mb-0.5">button text</label>
                  <input value={u.button_text || ''} onChange={(e) => setRow(i, 'button_text', e.target.value)} className="w-full px-1 border border-input rounded bg-background" />
                </div>
                <div>
                  <label className="block text-muted-foreground mb-0.5">image URL</label>
                  <input value={u.image_url || ''} onChange={(e) => setRow(i, 'image_url', e.target.value || null)} className="w-full px-1 border border-input rounded bg-background font-mono text-[10px]" />
                </div>
                <div className="col-span-2 text-right"><button onClick={() => remove(i)} className="text-destructive text-[10px]">remove upgrade</button></div>
              </div>
            ) : (
              <>
                <div className="font-medium">{u.heading}</div>
                {u.subtitle && <div className="text-muted-foreground">{u.subtitle}</div>}
                <div className="text-muted-foreground mt-1">target: <code>{u.target_rate_ref}</code></div>
                {u.benefits?.length > 0 && (
                  <ul className="mt-1 list-disc list-inside text-muted-foreground">
                    {u.benefits.map((b, k) => <li key={k}>{b}</li>)}
                  </ul>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
